import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Video, Handshake, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { LineReveal } from "@/components/LineReveal";
import { LeadForm } from "@/components/LeadForm";
import { BrandImage } from "@/components/Brand";
import { SERVICES } from "@/data/services";

const TOWNS = [
  "Maidstone", "Medway", "Canterbury", "Ashford", "Tunbridge Wells",
  "Dartford", "Folkestone", "Dover", "Sittingbourne", "Sevenoaks",
];

const WAYS = [
  {
    icon: Handshake,
    title: "Face to face when it matters",
    text: "Based at Suite 100, 7 Kenden Business Park in Rochester. Investigations, restructures and difficult meetings. For Kent businesses we can be in the room, not just on the phone.",
  },
  {
    icon: Phone,
    title: "Same-day by phone or video",
    text: "Most problems don't need a site visit. They need a straight answer today. You get one, wherever in Kent or the UK you're sat.",
  },
  {
    icon: Video,
    title: "UK-wide, Kent-rooted",
    text: "Half our work is fully remote. Kent is home and where we learned what small businesses actually need, and the support travels anywhere.",
  },
];

const LOCAL_CASES = [
  { tag: "Construction", title: "A 22-person Medway construction firm", result: "Contracts and handbook rebuilt in a fortnight; first grievance handled with a script, not a panic." },
  { tag: "Hospitality", title: "A Canterbury hospitality group", result: "Absence and rota issues untangled, managers trained to have the conversations early." },
  { tag: "Agency", title: "A Maidstone marketing agency scaling fast", result: "Startup package in, probation frameworks working, founders back to running the business." },
];

export default function Kent() {
  const origin = window.location.origin;

  return (
    <main data-testid="kent-page">
      <Seo
        title="HR Consultant Kent | Rochester-Based SME HR | Electra HR"
        siteName="Electra HR"
        description="Electra HR is a Rochester-based HR consultancy serving Kent and the UK. Same-day answers, fixed fees, no retainers, backed by SRA-regulated Bonsai Law."
        image="/api/og/kent.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "ProfessionalService",
              "@id": `${origin}/#kent`,
              name: "Electra HR, HR Consultant Kent",
              image: `${origin}/images/electra-hr-logo.png`,
              url: `${origin}/hr-consultant-kent`,
              telephone: "+44 1622 957300",
              email: "hello@electra-hr.com",
              priceRange: "£££",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Suite 100, 7 Kenden Business Park, Maritime Close",
                addressLocality: "Rochester",
                addressRegion: "Kent",
                postalCode: "ME2 4JF",
                addressCountry: "GB",
              },
              areaServed: [
                { "@type": "AdministrativeArea", name: "Kent" },
                { "@type": "Country", name: "United Kingdom" },
              ],
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: origin },
                { "@type": "ListItem", position: 2, name: "HR Consultant Kent", item: `${origin}/hr-consultant-kent` },
              ],
            },
          ],
        }}
      />

      <section className="grain relative overflow-hidden bg-ink pb-20 pt-32 sm:pt-40 text-paper">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <p className="eyebrow text-pink">HR consultant, Kent</p>
            <LineReveal
              className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] tracking-tight"
              lines={["HR support with", <span key="l2" className="text-pink">Kent roots.</span>]}
              delay={0.15}
            />
            <p className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-paper/70">
              Electra HR is based at Suite 100, 7 Kenden Business Park in Rochester and works with small businesses
              across Kent and <Link to="/hr-consultancy-uk" className="link-underline font-semibold text-paper hover:text-pink transition-colors">the whole UK</Link>. Local enough to sit across the table when it matters,
              and set up to move fast wherever you are.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="kent-cta-contact"
                className="group flex items-center gap-2 bg-pink px-7 py-4 font-bold text-ink transition-transform duration-300 hover:-translate-y-1"
              >
                Book a rapid HR call
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                data-testid="kent-cta-services"
                className="flex items-center gap-2 border-2 border-white/25 px-7 py-4 font-bold text-paper transition-colors hover:border-pink hover:text-pink"
              >
                See services
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:block">
            <Reveal delay={0.2}>
              <div className="clip-frame overflow-hidden border border-white/10">
                <BrandImage
                  src="/images/hero-laptop-900.webp"
                  srcSet="/images/hero-laptop-480.webp 480w, /images/hero-laptop-900.webp 900w"
                  sizes="(min-width: 1024px) 40vw, 100vw"
                  fallback="/images/hero-laptop.jpg"
                  alt="HR consultancy work in progress at Electra HR's Rochester office"
                  className="h-[380px] w-full object-cover"
                />
              </div>
              <p className="mt-4 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-paper/45">
                <MapPin size={14} className="text-pink" /> Suite 100, 7 Kenden Business Park, Maritime Close, Rochester, Kent ME2 4JF
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-paper py-16 sm:py-24">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow text-ink/50">Where we work</p>
            <h2 className="mt-4 max-w-2xl font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
              Proudly Kent. Comfortably national.
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink/70">
              Construction firms in Medway, agencies in Maidstone, hospitality in Canterbury and
              trades across the Weald. If your business has 5 to 50 people and a people problem,
              we're your HR department.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div data-testid="kent-towns" className="mt-10 flex flex-wrap gap-3">
              {TOWNS.map((t) => (
                <span key={t} className="border-2 border-ink/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-ink/60">
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {WAYS.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.08}>
                <div data-testid={`kent-way-${i}`} className="h-full border-2 border-ink p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[8px_8px_0_0_#0F0F1A]">
                  <w.icon size={26} className="text-ink" strokeWidth={1.8} />
                  <h3 className="mt-5 font-display text-xl font-semibold">{w.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/65">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink py-16 sm:py-24 text-paper">
        <div className="container-x">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow text-pink">What we fix for Kent SMEs</p>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
                Six services, all fixed-fee
              </h2>
            </div>
            <Link to="/services" data-testid="kent-services-link" className="group flex items-center gap-2 font-bold text-pink">
              All services <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 3) * 0.06} className="bg-ink">
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`kent-service-${s.slug}`}
                  className="group flex h-full flex-col p-7 transition-colors duration-300 hover:bg-ink-soft"
                >
                  <h3 className="font-display text-lg font-semibold group-hover:text-pink transition-colors">{s.title}</h3>
                  <p className="mt-2 text-sm text-paper/55">{s.tagline}</p>
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {LOCAL_CASES.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <article data-testid={`kent-case-${i}`} className="h-full border border-white/10 bg-ink-soft p-7">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-pink">{c.tag}</p>
                  <h3 className="mt-3 font-display text-lg font-semibold leading-snug">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-paper/55">{c.result}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 font-mono text-xs text-paper/35">
            Illustrative examples based on typical engagements. Named client stories are on the way.
          </p>
          <Reveal delay={0.1}>
            <p className="mt-10 max-w-3xl text-xs leading-relaxed text-paper/40">
              Electra HR is an HR consultancy, not a law firm, and is not SRA-regulated. When a matter
              needs legal advice, representation or privilege protection, our clients are handed straight
              into Bonsai Law, a separate, SRA-regulated firm, with full context and no handoff limbo.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <Link
              to="/hr-consultancy-uk"
              data-testid="kent-crosslink-uk"
              className="group mt-8 flex items-center justify-between gap-4 border border-white/10 bg-ink-soft p-6 transition-colors duration-300 hover:border-pink/60"
            >
              <div>
                <p className="eyebrow text-pink">Based outside Kent?</p>
                <p className="mt-2 text-sm text-paper/70">
                  We work exactly the same way across the whole UK. See how national support works.
                </p>
              </div>
              <ArrowRight size={22} className="shrink-0 text-pink transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="grain bg-pink py-16 sm:py-24 text-ink">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <p className="eyebrow text-ink/60">Local, human, fast</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.05]">
              Kent business? Start with a free initial conversation.
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-ink/70">
              No pressure, no hard sell. Tell us what's going on and you'll have a straight answer,
              plus a fixed quote if work is needed, within one working day.
            </p>
            <div className="mt-8 space-y-3 text-sm font-medium">
              <a href="tel:+441622957300" data-testid="kent-phone-link" className="flex items-center gap-3 hover:opacity-70 transition-opacity">
                <Phone size={17} /> 01622 957 300
              </a>
              <p className="flex items-center gap-3"><MapPin size={17} /> Suite 100, 7 Kenden Business Park, Maritime Close, Rochester, Kent ME2 4JF</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border-2 border-ink bg-paper p-7 sm:p-9 shadow-[10px_10px_0_0_#0F0F1A]">
              <LeadForm source="hr-consultant-kent" />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
